/**
 * Space Invaders Game
 * Shoot down descending aliens
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const SpaceInvaders = ({ onHome }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [score, setScore] = useLocalStorage('spaceinvaders_score', 0);
  const [highScore, setHighScore] = useLocalStorage('spaceinvaders_highScore', 0);

  const gameStateRef = useRef({
    player: { x: 175, width: 30, height: 30 },
    bullets: [],
    enemies: [],
    lives: 3,
  });

  const CANVAS_WIDTH = 400;
  const CANVAS_HEIGHT = 600;

  const startGame = () => {
    gameStateRef.current = {
      player: { x: 175, width: 30, height: 30 },
      bullets: [],
      enemies: Array.from({ length: 8 }, (_, i) => ({
        x: (i % 4) * 80 + 40,
        y: Math.floor(i / 4) * 60 + 30,
        width: 30,
        height: 30,
        active: true,
      })),
      lives: 3,
    };
    setScore(0);
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const resetGame = () => {
    if (score > highScore) setHighScore(score);
    setGameState(GAME_CONFIG.states.MENU);
  };

  useEffect(() => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const game = gameStateRef.current;

    const gameLoop = setInterval(() => {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Update bullets
      game.bullets = game.bullets.filter((bullet) => {
        bullet.y -= 7;
        return bullet.y > 0;
      });

      // Update enemies
      game.enemies.forEach((enemy) => {
        if (!enemy.active) return;
        enemy.y += 2;
        if (enemy.y > CANVAS_HEIGHT) {
          game.lives--;
          if (game.lives <= 0) {
            setGameState(GAME_CONFIG.states.GAME_OVER);
            if (score > highScore) setHighScore(score);
          }
          enemy.y = -30;
        }
      });

      // Collision detection
      game.bullets.forEach((bullet, bIdx) => {
        game.enemies.forEach((enemy) => {
          if (
            enemy.active &&
            bullet.x < enemy.x + enemy.width &&
            bullet.x + 5 > enemy.x &&
            bullet.y < enemy.y + enemy.height &&
            bullet.y + 15 > enemy.y
          ) {
            enemy.active = false;
            game.bullets.splice(bIdx, 1);
            setScore((prev) => prev + 100);
          }
        });
      });

      // Draw player
      ctx.fillStyle = GAME_CONFIG.colors.accent;
      ctx.fillRect(game.player.x, CANVAS_HEIGHT - 40, game.player.width, game.player.height);

      // Draw bullets
      ctx.fillStyle = GAME_CONFIG.colors.accent2;
      game.bullets.forEach((bullet) => {
        ctx.fillRect(bullet.x, bullet.y, 5, 15);
      });

      // Draw enemies
      ctx.fillStyle = GAME_CONFIG.colors.danger;
      game.enemies.forEach((enemy) => {
        if (enemy.active) {
          ctx.fillRect(enemy.x, enemy.y, enemy.width, enemy.height);
        }
      });
    }, 30);

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && game.player.x > 0) game.player.x -= 10;
      if (e.key === 'ArrowRight' && game.player.x < CANVAS_WIDTH - 30) game.player.x += 10;
      if (e.key === ' ') {
        e.preventDefault();
        game.bullets.push({ x: game.player.x + 12, y: CANVAS_HEIGHT - 40 });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      clearInterval(gameLoop);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [gameState, score, highScore]);

  return (
    <GameLayout
      title="Space Invaders 👾"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {gameState === GAME_CONFIG.states.MENU && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Space Invaders</h2>
            <p style={styles.menuDescription}>
              Destroy the aliens before they reach you!
            </p>
            <button onClick={startGame} className="btn btn-primary btn-lg" style={{ marginTop: '1rem' }}>
              🎮 Start Game
            </button>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && (
          <>
            <canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} style={styles.canvas} />
            <div style={styles.controls}>⬅️ Arrow Keys to Move | Space to Shoot</div>
          </>
        )}

        {gameState === GAME_CONFIG.states.GAME_OVER && (
          <div style={styles.gameOverCard}>
            <h1 style={styles.gameOverTitle}>Game Over</h1>
            <ScoreDisplay score={score} stats={{ 'Final Score': score }} />
            <button onClick={startGame} className="btn btn-primary mt-3" style={{ width: '100%' }}>
              🔄 Play Again
            </button>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
  },
  canvas: {
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: '4px',
    backgroundColor: '#000',
  },
  controls: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
  },
  gameOverCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    textAlign: 'center',
    maxWidth: '400px',
  },
  gameOverTitle: {
    color: GAME_CONFIG.colors.danger,
    marginBottom: '1rem',
    fontSize: '2rem',
  },
};

export default SpaceInvaders;
